/**
 * API Service for CDB-TQS Backend
 * Handles communication with the CDB-TQS knowledge management system
 * Uses MSAL Bearer token for authentication
 *
 * Barrel over the domain modules in src/services/cdb-tqs/ — the public
 * surface (cdbTqsApiService methods, exported types, standalone transcript
 * functions) is unchanged.
 */

import { knowledgeAreasApi } from "./cdb-tqs/knowledge-areas";
import { graphInstancesApi } from "./cdb-tqs/graph-instances";
import { ontologiesApi } from "./cdb-tqs/ontologies";
import { postgresInstancesApi } from "./cdb-tqs/postgres-instances";
import { documentsApi } from "./cdb-tqs/documents";
import { graphApi } from "./cdb-tqs/graph";
import { excelApi } from "./cdb-tqs/excel";
import { tokenUsageApi } from "./cdb-tqs/token-usage";
import { toolboxApi } from "./cdb-tqs/toolbox";
import { routerApi } from "./cdb-tqs/router";
import { attachmentsApi } from "./cdb-tqs/attachments";
import { personalFilesApi } from "./cdb-tqs/personal-files";
import { capabilitiesApi } from "./cdb-tqs/capabilities";
import { staffApi } from "./cdb-tqs/staff";
import { wissenshausApi } from "./cdb-tqs/wissenshaus";

/**
 * CDB-TQS API Client
 */
export const cdbTqsApiService = {
  ...knowledgeAreasApi,
  ...graphInstancesApi,
  ...ontologiesApi,
  ...postgresInstancesApi,
  ...documentsApi,
  ...graphApi,
  ...excelApi,
  ...tokenUsageApi,
  ...toolboxApi,
  ...routerApi,
  ...attachmentsApi,
  ...personalFilesApi,
  ...capabilitiesApi,
  ...staffApi,
  ...wissenshausApi,
};

export type {
  GraphInstance,
  GraphProvisioningStatus,
  AuraSize,
  CostEstimate,
  GraphInstanceSizeOption,
  GraphInstanceSizes,
  GraphInstanceUsage,
  ResizeGraphInstancePayload,
  CreateGraphInstancePayload,
  ProvisionGraphInstancePayload,
  UpdateGraphInstancePayload,
  TestGraphInstanceConnectionResult,
  ClientGraphMapping,
} from "./cdb-tqs/graph-instances";

export type {
  OntologyFieldType,
  OntologyField,
  OntologyTypeDef,
  OntologyEdgeTypeMapEntry,
  OntologyDefinition,
  SystemOntologyKey,
  OntologySummary,
  Ontology,
  OntologyListResponse,
  SaveOntologyPayload,
  OntologyValidation,
} from "./cdb-tqs/ontologies";

export type {
  PostgresInstance,
  CreatePostgresInstancePayload,
  UpdatePostgresInstancePayload,
  TestPostgresConnectionResult,
  TestPostgresConnectionPayload,
  ClientPostgresMapping,
  AssignClientPayload,
} from "./cdb-tqs/postgres-instances";

export type {
  KARelationsMatrixKA,
  KARelatedEntry,
  KARelationsMatrixResponse,
  TokenUsageSummary,
  TokenUsageByModel,
  TokenUsageByProvider,
  TokenUsageSource,
  UsageDimension,
  UsageQueryRequest,
  UsageRow,
  TokenUsageByCallType,
  TokenUsageByKA,
  TokenUsageByClient,
  TokenUsageDailyPoint,
  TokenLimitSetRequest,
  TokenLimitResponse,
  UserTokenUsage,
  ByUserResponse,
  UserLimitSetRequest,
  UserLimitResponse,
  MyUsageResponse,
  QuotaRequest,
  QuotaRequestScope,
  QuotaRequestStatus,
  QuotaRequestListResponse,
  QuotaDecision,
} from "./cdb-tqs/token-usage";

export type {
  PersonalFileVersion,
  CollaboraConfig,
  PersonalFileListItem,
  PersonalFilePdfExport,
  PersonalFilesListResponse,
  PersonalFileShareLink,
  PersonalFileShareView,
  PersonalFileShareState,
  SharedFileInfo,
  BrainDocumentState,
  BrainDocumentStatus,
  BrainFileState,
  BrainFileStatus,
  BrainStatusResponse,
} from "./cdb-tqs/personal-files";

export type { MyFileItem, MyFilesResponse } from "./cdb-tqs/attachments";

export type {
  CapabilityDef,
  CapabilityRegistry,
  CapabilityTier,
  CapabilityValue,
  CapabilityValueType,
  CapabilityOverride,
  ClientAssignments,
  ClientProductAssignment,
  AssignmentMode,
  AssignmentPutPayload,
  TierCreatePayload,
  TierUpdatePayload,
  MyCapabilities,
  ResolvedClientCapabilities,
  SkillRoleRestriction,
} from "./cdb-tqs/capabilities";

export type {
  StaffElevation,
  StaffElevationStatus,
  StaffElevationCreatePayload,
} from "./cdb-tqs/staff";

export { refineTranscript } from "./cdb-tqs/transcripts";
export type {
  RefineTranscriptResponse,
  RefineOptions,
  RefineProgress,
  RefineStopReason,
} from "./cdb-tqs/transcripts";

export default cdbTqsApiService;
